import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, Plus, Search, Star, Copy, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { mccGet, mccPatch, mccDelete } from './mccApi';
import { SectionHeader, EmptyState, PlatformIcon } from './mccUi';
import { PLATFORMS, CONTENT_TYPES } from './mccConstants';
import MccCreateDialog from './MccCreateDialog';

export default function MccHooks({ team }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ platform: '', content_type: '', used: '', approved: '', q: '' });
  const [openDialog, setOpenDialog] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (params.used === '') delete params.used; else params.used = params.used === 'true';
      if (params.approved === '') delete params.approved; else params.approved = params.approved === 'true';
      const res = await mccGet('/marketing/hooks', params);
      setItems(res.items || []);
    } catch (e) { toast.error(e.message || 'Failed to load hooks'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.platform, filters.content_type, filters.used, filters.approved]);

  const filtered = useMemo(() => {
    if (!filters.q) return items;
    const ql = filters.q.toLowerCase();
    return items.filter((h) =>
      (h.hook || '').toLowerCase().includes(ql) ||
      (h.topic || '').toLowerCase().includes(ql) ||
      (h.notes || '').toLowerCase().includes(ql)
    );
  }, [items, filters.q]);

  const toggleApproved = async (h) => {
    try { await mccPatch(`/marketing/hooks/${h.id}`, { approved: !h.approved }); load(); }
    catch (e) { toast.error(e.message || 'Failed'); }
  };
  const toggleUsed = async (h) => {
    try { await mccPatch(`/marketing/hooks/${h.id}`, { used: !h.used }); load(); }
    catch (e) { toast.error(e.message || 'Failed'); }
  };
  const del = async (h) => {
    if (!window.confirm(`Delete hook?\n\n"${h.hook}"`)) return;
    try { await mccDelete(`/marketing/hooks/${h.id}`); load(); toast.success('Hook deleted'); }
    catch (e) { toast.error(e.message || 'Failed'); }
  };
  const copyHook = (h) => {
    navigator.clipboard.writeText(h.hook || '').then(() => {
      setCopied(h.id);
      setTimeout(() => setCopied(null), 1200);
    });
  };

  const setRating = async (h, n) => {
    try { await mccPatch(`/marketing/hooks/${h.id}`, { rating: n }); load(); }
    catch (e) { toast.error(e.message || 'Failed'); }
  };

  return (
    <div className="p-6 space-y-4">
      <SectionHeader
        title="Hook Library"
        subtitle="A searchable catalogue of proposed, approved and used hooks. One click to reuse in any project."
        icon={Sparkles}
        right={<Button onClick={() => setOpenDialog(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="mcc-hook-create">
          <Plus className="w-4 h-4 mr-1" /> Add Hook
        </Button>}
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input className="pl-8 w-64" placeholder="Search hooks…" value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
        </div>
        <Select value={filters.platform || '__all__'} onValueChange={(v) => setFilters((f) => ({ ...f, platform: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="h-9 w-36"><SelectValue placeholder="Platform" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All platforms</SelectItem>
            {PLATFORMS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.content_type || '__all__'} onValueChange={(v) => setFilters((f) => ({ ...f, content_type: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="h-9 w-40"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All types</SelectItem>
            {CONTENT_TYPES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.approved || '__any__'} onValueChange={(v) => setFilters((f) => ({ ...f, approved: v === '__any__' ? '' : v }))}>
          <SelectTrigger className="h-9 w-36"><SelectValue placeholder="Approval" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Any</SelectItem>
            <SelectItem value="true">Approved</SelectItem>
            <SelectItem value="false">Not approved</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.used || '__any__'} onValueChange={(v) => setFilters((f) => ({ ...f, used: v === '__any__' ? '' : v }))}>
          <SelectTrigger className="h-9 w-32"><SelectValue placeholder="Usage" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Any</SelectItem>
            <SelectItem value="true">Used</SelectItem>
            <SelectItem value="false">Unused</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 p-8 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Sparkles} title="No hooks yet" body="Add hooks the team can search, rate and reuse."
          cta={<Button onClick={() => setOpenDialog(true)}>Add first hook</Button>} />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((h) => (
            <div key={h.id} className="rounded-xl border border-slate-200 bg-white p-4 hover:shadow-md relative">
              <div className="flex items-start justify-between gap-2 mb-2">
                {h.platform ? <PlatformIcon platform={h.platform} size={22} /> : <Sparkles className="w-5 h-5 text-indigo-500" />}
                <div className="flex items-center gap-1">
                  <button onClick={() => toggleApproved(h)} className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${h.approved ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {h.approved ? 'APPROVED' : 'PROPOSED'}
                  </button>
                  <button onClick={() => toggleUsed(h)} className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${h.used ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`}>
                    {h.used ? 'USED' : 'UNUSED'}
                  </button>
                </div>
              </div>
              <p className="text-[15px] italic font-medium text-slate-900 leading-snug">&ldquo;{h.hook}&rdquo;</p>
              {h.topic && <p className="text-[11px] text-slate-500 mt-2">Topic: <b>{h.topic}</b></p>}
              {h.target_audience && <p className="text-[11px] text-slate-500">Audience: <b>{h.target_audience}</b></p>}
              {h.best_delivered_by && <p className="text-[11px] text-slate-500">Best delivered by: <b>{h.best_delivered_by}</b></p>}
              {h.notes && <p className="text-[11px] text-slate-500 mt-2">{h.notes}</p>}
              <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => setRating(h, n === (h.rating || 0) ? null : n)}>
                      <Star className={`w-3.5 h-3.5 ${n <= (h.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => copyHook(h)} className="p-1.5 text-slate-500 hover:text-slate-800 rounded" title="Copy">
                    {copied === h.id ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => del(h)} className="text-[11px] text-rose-500 hover:underline">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <MccCreateDialog open={openDialog} onClose={() => setOpenDialog(false)} kind="hook" team={team} onCreated={load} />
    </div>
  );
}
